import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useHistory } from 'react-router-dom';
import { MODAL_KEYS, PACKAGE_CHARGE_TYPE } from '../../../../../utilities/constants';
import Loyalty from '../../../../Loyalty/Loyalty';
import { LOYALTY_TYPES } from '../../../../Loyalty/utilities/constants';
import { getTimeRemaining } from '../../../../TimerModalContent/utilities/timerFunctions';
import { Box } from '../../../../ui-kit';
import theme from '../../../../ui-kit/themes/theme';
import { handleTotal, handleTotalWithTaxes } from '../../../../utilities/utils';
import { SummaryAccordion } from '../../Accordion';
import CartOverlay from './CartOverlay';
import { SuitePlanContent } from './Content';
import CartLogo from './Content/CartLogo';
import EmptyCartContent from './Content/EmptyCartContent';
import { Header } from './Header';

const propTypes = {
  /** Flag to indicate if the cost summary should start open */
  shouldStartOpened: PropTypes.bool,

  /** Flag to indicat if the cost summary is on the payment page */
  onPaymentPage: PropTypes.bool
};

export const Summary = ({
  shouldStartOpened,
  onPaymentPage,
  openModal,
  showLoyaltyDisclaimerOnly,
  isHighestRatesEnabled,
  avoidCartLogoRender,
  isModifyFlow,
  hiddenOfferPrefixes,
  appliedViaPromoBanner,
  ...restProps
}) => {
  // Overlay State
  const [overlayVisible, setOverlayVisible] = useState(false);
  // Handle Open Cart
  const handleOpenCart = () => {
    setOverlayVisible(prev => !prev);
  };

  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.md})`
  });

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.xl });

  const history = useHistory();

  const {
    summaryTotal,
    userOffers,
    activeOffers,
    showLoyalty,
    loyaltyOptIn,
    suiteTotal,
    currentSelectedPackages,
    isUserAuthenticated,
    taxesAndFees,
    roomAdjustmentTax,
    selectedSuite,
    resortLocationUrlParam,
    isCartVariation,
    onCmpPage,
    onBirthDayPage,
    onDaypassPage,
    onSeasonpassPage,
    resortLocation,
    cartResortLocation,
    clearSelectedSuite,
    clearSelectedPackages,
    resetCartResortLocation,
    setExpiredCart,
    isCartExpired,
    loyaltyEarnThresholdNights,
    setIsCartOpenClose,
    savePointsForLater,
    pointAmount
  } = restProps;

  const isCartShown =
    isCartVariation && !avoidCartLogoRender && !onCmpPage && !onBirthDayPage && !onDaypassPage && !onSeasonpassPage;

  const totalWithOrWithoutTaxesAuth = () => {
    return !onPaymentPage
      ? handleTotal(
          userOffers,
          activeOffers,
          showLoyalty,
          loyaltyOptIn,
          savePointsForLater ? 0 : pointAmount,
          suiteTotal,
          currentSelectedPackages
        )?.toFixed(2)
      : handleTotalWithTaxes(
          userOffers,
          activeOffers,
          showLoyalty,
          loyaltyOptIn,
          savePointsForLater ? 0 : pointAmount,
          suiteTotal,
          taxesAndFees,
          roomAdjustmentTax,
          currentSelectedPackages
        );
  };

  const handleSummaryHeaderTotalAmount = () => {
    return Number(isUserAuthenticated ? totalWithOrWithoutTaxesAuth() : summaryTotal?.toFixed(2));
  };

  // Cart Has at least suite
  const cartHasSuite = selectedSuite && Object.keys(selectedSuite).length > 0;
  // Count of packages in the cart
  let packagesCount = 0;
  if (isCartVariation && cartHasSuite) {
    packagesCount = currentSelectedPackages.reduce((acc, packageItem) => {
      if (packageItem.chargeType === PACKAGE_CHARGE_TYPE.personPerDay) {
        return acc + 1;
      }
      if (packageItem.quantity) {
        return acc + Number(packageItem.quantity);
      }
      return acc;
    }, 1);
  }

  // Block body scroll if overlay is visible and mobile
  if (isCartVariation && isMobile) {
    if (overlayVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }

  // Check if there is a PUSH action on history and close the cart
  if (isCartVariation) {
    history.listen(action => {
      if (action === 'PUSH') {
        setOverlayVisible(false);
      }
    });
  }

  // Open Modal for modify cart item
  const openModifyCartItemModal = (item, type, variation, confirmationFunction) => {
    openModal(
      {
        keyContentComponent: MODAL_KEYS.CART_MODIFY_MODAL,
        params: {
          item,
          type,
          variation,
          confirmationFunction
        }
      },
      false
    );
  };

  // Clear selected suite and packages if cart location is different from resort location
  useEffect(() => {
    if (!isCartVariation || !cartResortLocation) return;
    if (cartResortLocation !== resortLocation) {
      clearSelectedSuite();
      clearSelectedPackages();
      resetCartResortLocation();
    }
  }, [
    cartResortLocation,
    clearSelectedPackages,
    clearSelectedSuite,
    isCartVariation,
    resetCartResortLocation,
    resortLocation
  ]);

  // expiration timer
  useEffect(() => {
    // Dont run if not on cart version
    if (!isCartVariation) return;
    // Dont run if no suite selected
    if (!selectedSuite || isCartExpired) return;
    const expirationTime = localStorage.getItem('timerObj');
    // Dont run if no expiration time, this is set byt the timer on Plan page
    if (!expirationTime) return;
    const countdown = setInterval(() => {
      const timer = getTimeRemaining(expirationTime);

      if (timer.total < 0) {
        setExpiredCart();
        clearSelectedSuite();
        clearSelectedPackages();
        clearInterval(countdown);

        return;
      }
    }, 1000);
    return () => {
      clearInterval(countdown);
    };
  }, [clearSelectedPackages, clearSelectedSuite, isCartExpired, isCartVariation, selectedSuite, setExpiredCart]);

  if (showLoyaltyDisclaimerOnly) {
    return (
      <Box mx={3}>
        <Loyalty
          loyaltyType={LOYALTY_TYPES.earn}
          numberOfNights={restProps.numberOfNights}
          isUserAuthenticated={isUserAuthenticated}
          suitNightRate={restProps.suiteTotal}
          pointsMultiplier={restProps.getPointsMultiplier}
          loyaltyEarnThresholdNights={loyaltyEarnThresholdNights}
          {...restProps}
        />
      </Box>
    );
  }

  return (
    <SummaryAccordion
      style={
        !isCartVariation
          ? { border: isMobile ? 'none' : '1px solid #CCF3F4', borderRadius: isMobile ? '0 0 24px 24px' : '24px' }
          : null
      }
      shouldStartOpened={!isMobile && onPaymentPage ? false : shouldStartOpened}>
      {!isCartVariation ? (
        <SummaryAccordion.Header>
          <Header onPaymentPage={onPaymentPage} total={handleSummaryHeaderTotalAmount()} />
        </SummaryAccordion.Header>
      ) : null}
      {/* Cart logo */}
      {isCartShown ? (
        <CartLogo
          toggleOpen={handleOpenCart}
          packagesCount={packagesCount}
          isMobile={isMobile}
          isTablet={isTablet}
          summaryTotal={summaryTotal}
          overlayVisible={overlayVisible}
        />
      ) : null}
      {/* Suite Plan Content */}
      <CartOverlay
        {...restProps}
        setIsCartOpenClose={setIsCartOpenClose}
        cartHasPackages={cartHasSuite}
        overlayVisible={overlayVisible}
        toggleOpen={handleOpenCart}
        shouldSavePoints={savePointsForLater}>
        {/* Empty Cart */}
        {isCartVariation && (!cartHasSuite || isCartExpired) ? (
          <EmptyCartContent resortLocationUrlParam={resortLocationUrlParam} isCartExpired={isCartExpired} />
        ) : (
          <SummaryAccordion.Content isCartVariation={isCartVariation}>
            {({ expanded }) => {
              return (
                <SuitePlanContent
                  expanded={expanded}
                  onPaymentPage={onPaymentPage}
                  openModifyCartItemModal={openModifyCartItemModal}
                  toggleOpen={handleOpenCart}
                  clearSelectedSuite={clearSelectedSuite}
                  isHighestRatesEnabled={isHighestRatesEnabled}
                  isModifyFlow={isModifyFlow}
                  hiddenOfferPrefixes={hiddenOfferPrefixes}
                  appliedViaPromoBanner={appliedViaPromoBanner}
                  shouldSavePoints={savePointsForLater}
                  {...restProps}
                />
              );
            }}
          </SummaryAccordion.Content>
        )}
      </CartOverlay>
    </SummaryAccordion>
  );
};

Summary.propTypes = propTypes;

Summary.defaultProps = {
  shouldStartOpened: true
};
